import { useState, useRef } from 'react'
import { Upload, X, FileText, Image, FileSpreadsheet, Presentation, Archive, Music, Video, AlertCircle, CheckCircle, Loader2 } from 'lucide-react'
import { supabase } from '../services/supabase'
import type { MaterialAula, UploadProgress } from '../services/supabase'
import { useToast } from '../hooks/useToast'

interface MaterialUploadProps {
    aulaId: string
    onUploadSuccess: (material: MaterialAula) => void
    onUploadError?: (error: string) => void
    maxFileSize?: number // em MB
    allowedTypes?: string[]
    className?: string
}

// Mapeamento de tipos de arquivo para ícones
const getFileIcon = (tipo: string) => {
    switch (tipo.toLowerCase()) {
        case 'pdf':
            return <FileText className="w-5 h-5 text-red-500" />
        case 'docx':
        case 'doc':
            return <FileText className="w-5 h-5 text-blue-500" />
        case 'xlsx':
        case 'xls':
            return <FileSpreadsheet className="w-5 h-5 text-green-500" />
        case 'pptx':
        case 'ppt':
            return <Presentation className="w-5 h-5 text-orange-500" />
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
            return <Image className="w-5 h-5 text-purple-500" />
        case 'mp4':
        case 'avi':
        case 'mov':
            return <Video className="w-5 h-5 text-indigo-500" />
        case 'mp3':
        case 'wav':
            return <Music className="w-5 h-5 text-pink-500" />
        case 'zip':
        case 'rar':
        case '7z':
            return <Archive className="w-5 h-5 text-gray-500" />
        default:
            return <FileText className="w-5 h-5 text-gray-500" />
    }
}

// Obter tipo de arquivo baseado na extensão
const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase() || ''
    return extension
}

// Formatar tamanho do arquivo
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export default function MaterialUpload({
    aulaId,
    onUploadSuccess,
    onUploadError,
    maxFileSize = 50, // 50MB por padrão
    allowedTypes = ['pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'jpg', 'jpeg', 'png', 'gif', 'mp4', 'avi', 'mov', 'mp3', 'wav', 'zip', 'rar', '7z'],
    className
}: MaterialUploadProps) {
    const [isDragging, setIsDragging] = useState(false)
    const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [descriptions, setDescriptions] = useState<Record<string, string>>({})
    const fileInputRef = useRef<HTMLInputElement>(null)
    const { showSuccess, showError } = useToast()

    // Validar arquivo
    const validateFile = (file: File): string | null => {
        // Verificar tamanho
        if (file.size > maxFileSize * 1024 * 1024) {
            return `Arquivo ${file.name} excede o tamanho máximo de ${maxFileSize}MB`
        }

        // Verificar tipo
        const fileType = getFileType(file.name)
        if (!allowedTypes.includes(fileType)) {
            return `Tipo de arquivo ${fileType} não é permitido`
        }

        return null
    }

    // Manipular seleção de arquivos
    const handleFileSelect = (files: FileList | null) => {
        if (!files) return

        const newFiles: File[] = []
        const errors: string[] = []

        Array.from(files).forEach(file => {
            const error = validateFile(file)
            if (error) {
                errors.push(error)
            } else {
                newFiles.push(file)
            }
        })

        if (errors.length > 0) {
            showError(errors.join('\n'))
        }

        if (newFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...newFiles])
        }
    }

    // Manipular drag and drop
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
        handleFileSelect(e.dataTransfer.files)
    }

    // Remover arquivo da lista
    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    }

    // Atualizar descrição
    const updateDescription = (index: number, description: string) => {
        setDescriptions(prev => ({
            ...prev,
            [index]: description
        }))
    }

    // Upload de arquivo individual - IGUAL AO UPLOAD DE VÍDEO
    const uploadFile = async (file: File, description?: string): Promise<MaterialAula> => {
        const fileType = getFileType(file.name)
        const randomName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_') // Sanitizar nome
        const fileName = `${Date.now()}-${randomName}`

        try {
            // Fazer upload para o Supabase Storage - IGUAL AO VÍDEO
            const { error: uploadError } = await supabase.storage
                .from('material-aula')
                .upload(fileName, file)

            if (uploadError) throw uploadError

            // Obter URL pública do arquivo - IGUAL AO VÍDEO
            const { data: urlData } = supabase.storage
                .from('material-aula')
                .getPublicUrl(fileName)

            const publicUrl = urlData.publicUrl

            // Criar registro na tabela material_aula
            const materialData = {
                aula_id: aulaId,
                nome_arquivo: fileName,
                nome_original: file.name,
                tipo_arquivo: fileType,
                tamanho_arquivo: file.size,
                url_arquivo: publicUrl,
                bucket_name: 'material-aula',
                descricao: description || null,
                ordem_exibicao: 1,
                status: 'ativo'
            }

            const { data, error } = await supabase
                .from('material_aula')
                .insert(materialData)
                .select()
                .single()

            if (error) throw error

            return data
        } catch (error) {
            console.error('Erro no upload:', error)
            throw error
        }
    }

    // Upload de todos os arquivos
    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            showError('Selecione pelo menos um arquivo para upload')
            return
        }

        setUploadProgress({ progress: 0, status: 'uploading', message: 'Iniciando upload...' })

        try {
            let successCount = 0
            const totalFiles = selectedFiles.length

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i]
                const description = descriptions[i] || ''

                try {
                    const material = await uploadFile(file, description)
                    onUploadSuccess(material)
                    successCount++

                    // Atualizar progresso
                    const progress = Math.round(((i + 1) / totalFiles) * 100)
                    setUploadProgress({
                        progress,
                        status: 'uploading',
                        message: `Enviando arquivo ${i + 1} de ${totalFiles}...`
                    })
                } catch (error) {
                    console.error(`Erro ao fazer upload de ${file.name}:`, error)
                    if (onUploadError) {
                        onUploadError(`Erro ao fazer upload de ${file.name}: ${error}`)
                    }
                }
            }

            // Limpar arquivos selecionados
            setSelectedFiles([])
            setDescriptions({})

            if (successCount === totalFiles) {
                setUploadProgress({ progress: 100, status: 'success', message: `${successCount} arquivo(s) enviado(s) com sucesso!` })
                showSuccess(`${successCount} arquivo(s) enviado(s) com sucesso!`)
            } else if (successCount > 0) {
                setUploadProgress({ progress: 100, status: 'success', message: `${successCount} de ${totalFiles} arquivo(s) enviado(s) com sucesso!` })
                showSuccess(`${successCount} de ${totalFiles} arquivo(s) enviado(s) com sucesso!`)
            } else {
                setUploadProgress({ progress: 100, status: 'error', message: 'Nenhum arquivo foi enviado com sucesso!' })
                showError('Nenhum arquivo foi enviado com sucesso!')
            }

        } catch (error) {
            setUploadProgress({ progress: 0, status: 'error', message: 'Erro durante o upload!' })
            showError('Erro durante o upload!')
        }
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Área de Drop */}
            <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Arraste arquivos aqui ou clique para selecionar
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Tipos permitidos: {allowedTypes.join(', ')}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                    Tamanho máximo: {maxFileSize}MB por arquivo
                </p>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept={allowedTypes.map(type => `.${type}`).join(',')}
                    onChange={(e) => handleFileSelect(e.target.files)}
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    Selecionar Arquivos
                </button>
            </div>

            {/* Lista de Arquivos Selecionados */}
            {selectedFiles.length > 0 && (
                <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        Arquivos Selecionados ({selectedFiles.length})
                    </h4>
                    {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            {getFileIcon(getFileType(file.name))}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {file.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatFileSize(file.size)}
                                </p>
                            </div>
                            <input
                                type="text"
                                placeholder="Descrição (opcional)"
                                value={descriptions[index] || ''}
                                onChange={(e) => updateDescription(index, e.target.value)}
                                className="flex-1 px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                            />
                            <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Progresso do Upload */}
            {uploadProgress && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                            {uploadProgress.message}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                            {uploadProgress.progress}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                            className={`h-2 rounded-full transition-all duration-300 ${uploadProgress.status === 'success'
                                ? 'bg-green-500'
                                : uploadProgress.status === 'error'
                                    ? 'bg-red-500'
                                    : 'bg-indigo-500'
                                }`}
                            style={{ width: `${uploadProgress.progress}%` }}
                        />
                    </div>
                    {uploadProgress.status === 'success' && (
                        <div className="flex items-center text-green-600 dark:text-green-400">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm">Upload concluído!</span>
                        </div>
                    )}
                    {uploadProgress.status === 'error' && (
                        <div className="flex items-center text-red-600 dark:text-red-400">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm">Erro no upload</span>
                        </div>
                    )}
                </div>
            )}

            {/* Botão de Upload */}
            {selectedFiles.length > 0 && (
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleUpload}
                        disabled={uploadProgress?.status === 'uploading'}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                    >
                        {uploadProgress?.status === 'uploading' && (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                        <span>
                            {uploadProgress?.status === 'uploading' ? 'Enviando...' : 'Enviar Arquivos'}
                        </span>
                    </button>
                </div>
            )}
        </div>
    )
}
